import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLabels } from 'wsm-common-data';
import { formatCurrency } from 'ddc-intl';
import SingleItem from './SingleItem';
import './styles.scss';

const MAX_PACKAGES_DISPLAY = 3;

const ListStylePackages = ({
	packages,
	options,
	accessories,
	locale,
	onOpenModal,
	togglePackageDetails,
	prefs,
	isModal,
	widgetInfo
}) => {
	const labels = useLabels();
	const showPricing = useSelector((state) => state.settings.showPricing);

	const hasPackages = packages && packages.length > 0;
	const hasOptions = isModal && options && options.length > 0;

	const displayPackages = isModal
		? packages
		: packages.slice(0, MAX_PACKAGES_DISPLAY);
	const hasMorePackages = !isModal && packages.length > MAX_PACKAGES_DISPLAY;
	const totalOptionsPrice = useMemo(
		() => options.reduce((sum, item) => sum + (item.price || 0), 0),
		[options]
	);
	const totalItemsCount = useMemo(
		() =>
			displayPackages.length +
			(options.length > 0 ? 1 : 0) +
			accessories.length,
		[displayPackages.length, options.length, accessories.length]
	);

	if (
		(!packages || packages.length === 0) &&
		(!options || options.length === 0) &&
		(!accessories || accessories.length === 0)
	) {
		return null;
	}

	const isCollapseEnabled = prefs?.toggleCollapse === 'true';
	const listStylePackagesClassName = [
		'list-style-packages',
		isCollapseEnabled && 'list-style-packages-collapse-enabled',
		isModal && 'list-style-packages-modal'
	].filter(Boolean).join(' ');

	return (
		<div className={listStylePackagesClassName}>
			{isModal && hasPackages && (
				<h4 className="mb-4 mt-0">
					{labels.get('INCLUDED_PACKAGES')}
				</h4>
			)}
			{displayPackages.map((pkg, index) => (
				<SingleItem
					key={pkg.name || index}
					index={index}
					itemName={pkg.name}
					subItems={pkg.subitems || []}
					itemType="package"
					itemPrice={
						pkg.price > 0 ? formatCurrency(pkg.price, locale) : '0'
					}
					onOpenModal={onOpenModal}
					totalItemsCount={totalItemsCount}
					isFirstItem={index === 0}
					isEnableDisclaimer={prefs?.enableDisclaimers === 'true'}
					layout={prefs?.packagesOptionsLayout || 'list'}
					togglePackageDetails={togglePackageDetails}
					isModal={isModal}
					widgetInfo={widgetInfo}
				/>
			))}
			{!isModal && options.length > 0 && (
				<SingleItem
					itemType="option"
					itemName={labels.get('MORE_PRE_INSTALLED_ITEMS')}
					subItems={options}
					itemPrice={
						totalOptionsPrice > 0
							? formatCurrency(totalOptionsPrice, locale)
							: '0'
					}
					onOpenModal={onOpenModal}
					totalItemsCount={totalItemsCount}
					isFirstItem={displayPackages.length === 0}
					isEnableDisclaimer={prefs?.enableDisclaimers === 'true'}
					layout={prefs?.packagesOptionsLayout || 'list'}
					togglePackageDetails={togglePackageDetails}
				/>
			)}
			{accessories.map((accessory, index) => (
				<SingleItem
					key={accessory.name || `accessory-${index}`}
					index={index}
					itemName={accessory.name}
					itemType="accessories"
					itemPrice={
						accessory.price > 0
							? formatCurrency(accessory.price, locale)
							: '0'
					}
					onOpenModal={onOpenModal}
					totalItemsCount={totalItemsCount}
					isFirstItem={
						displayPackages.length === 0 &&
						options.length === 0 &&
						index === 0
					}
					isEnableDisclaimer={prefs?.enableDisclaimers === 'true'}
					layout={prefs?.packagesOptionsLayout || 'list'}
					togglePackageDetails={togglePackageDetails}
				/>
			))}
			{hasMorePackages && (
				<div className="see-all-container">
					<button
						type="button"
						className="btn btn-link px-0"
						onClick={onOpenModal}
					>
						{labels.get('SEE_ALL_PACKAGES_OPTIONS')}
					</button>
				</div>
			)}
			{hasOptions && (
				<div className="list-options-section mt-6">
					<h4 className="mb-4 mt-0">
						{labels.get('INCLUDED_OPTIONS')}
					</h4>
					<ul className="list-unstyled list-style-options">
						{options.map(({ name, price }, idx) => (
							<li
								key={name || idx}
								className="list-style-option-item py-2"
							>
								<span>{name}</span>
								{showPricing && price > 0 && (
									<div className="package-value mt-3">
										{`${formatCurrency(price, locale)} ${(labels.get('VALUE') || 'value').toLowerCase()
											}`}
									</div>
								)}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

ListStylePackages.defaultProps = {
	packages: [],
	options: [],
	accessories: [],
	locale: 'en_US',
	togglePackageDetails: false,
	prefs: {},
	isModal: false,
	widgetInfo: null
};

ListStylePackages.propTypes = {
	packages: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			subitems: PropTypes.arrayOf(PropTypes.shape({})),
			price: PropTypes.number
		})
	),
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			price: PropTypes.number
		})
	),
	accessories: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			price: PropTypes.number
		})
	),
	locale: PropTypes.string,
	onOpenModal: PropTypes.func,
	togglePackageDetails: PropTypes.bool,
	prefs: PropTypes.shape({
		toggleCollapse: PropTypes.string,
		enableDisclaimers: PropTypes.string,
		packagesOptionsLayout: PropTypes.string,
	}),
	isModal: PropTypes.bool,
	widgetInfo: PropTypes.shape({
		dealerId: PropTypes.string,
		vehicleId: PropTypes.string
	})
};

export default ListStylePackages;
